import React, { useState, useEffect, useRef } from 'react';
import { Bot, X, Send, ShieldCheck } from 'lucide-react';
import { api } from '../utils/apiClient';

const getGeminiResponse = async (query) => {
  try {
    const data = await api.post('/agente_lex', { mensaje: query });
    return data && data.respuesta ? data.respuesta : "No hay respuesta disponible.";
  } catch (error) {
    return "Error de conexión con el asistente.";
  }
};

const LegalBotChat = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { text: "¡Hola! Soy Omnia, tu asistente jurídico inteligente. ¿En qué puedo ayudarte?", sender: 'bot' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (input.trim() === '') return;
    const currentInput = input;
    setMessages(prev => [...prev, { text: currentInput, sender: 'user' }]);
    setInput('');
    setIsLoading(true);

    const response = await getGeminiResponse(currentInput);
    setMessages(prev => [...prev, { text: response, sender: 'bot' }]);
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[400px] z-[999] flex flex-col animate-in slide-in-from-right duration-300">
      
      {/* CONTENEDOR PRINCIPAL */}
      <div className="flex-1 m-4 flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-slate-900/95 backdrop-blur-xl shadow-2xl">
        
        {/* HEADER */}
        <div className="p-5 bg-gradient-to-r from-blue-600/20 to-indigo-600/20 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="p-2.5 bg-blue-600 rounded-2xl shadow-lg shadow-blue-500/30">
                <Bot size={22} className="text-white" />
              </div>
              <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-slate-900 rounded-full"></span>
            </div>
            <div>
              <h3 className="font-bold text-white text-base tracking-tight">Omnia</h3>
              <p className="text-[11px] text-blue-400 font-medium uppercase tracking-wider">Asistente LegalPlus</p>
            </div>
          </div>
          
          {/* BOTÓN X CORREGIDO */}
          <button
            onClick={(e) => {
              e.preventDefault();
              onClose();
            }}
            className="p-2 hover:bg-white/10 rounded-xl transition-all text-slate-400 hover:text-white cursor-pointer"
            title="Cerrar chat"
          >
            <X size={24} />
          </button>
        </div>

        {/* CUERPO */}
        <div 
          ref={scrollRef}
          className="flex-1 p-5 overflow-y-auto space-y-6 custom-scrollbar"
        >
          {messages.map((msg, index) => (
            <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`px-4 py-3 rounded-2xl max-w-[85%] text-sm leading-relaxed shadow-sm ${
                msg.sender === 'user'
                  ? 'bg-blue-600 text-white rounded-tr-none'
                  : 'bg-white/5 text-slate-200 border border-white/10 rounded-tl-none'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white/5 p-4 rounded-2xl rounded-tl-none border border-white/10">
                <div className="flex space-x-1.5">
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                  <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* INPUT */}
        <div className="p-5 bg-white/[0.02] border-t border-white/10">
          <div className="relative flex items-center bg-slate-800/50 rounded-2xl border border-white/10 focus-within:border-blue-500/50 transition-all p-1.5">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Haz una consulta a Omnia..."
              className="flex-1 bg-transparent border-none focus:ring-0 text-white placeholder-slate-500 text-sm px-3"
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              className="p-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-xl transition-all shadow-lg"
            >
              <Send size={18} />
            </button>
          </div>
          <p className="text-[10px] text-slate-500 mt-3 text-center flex items-center justify-center gap-1 uppercase tracking-tighter">
             <ShieldCheck size={10} /> Consultas Protegidas por Omnia AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default LegalBotChat;